import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiSearch, FiBriefcase, FiArrowRight, FiCheckCircle, FiInbox, FiArrowLeft, FiCalendar, FiTag, FiEye } from 'react-icons/fi';

const API_URL = `${process.env.REACT_APP_API_URL}/api/case-studies`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

function CaseStudiesPublic() {
  const { id } = useParams();
  const [caseStudies, setCaseStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [singleStudy, setSingleStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (id) {
      fetchSingleCaseStudy(id);
    } else {
      fetchCaseStudies();
    }
  }, [id]);

  const fetchSingleCaseStudy = async (studyId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?id=${studyId}`);
      if (response.data.success && response.data.data) {
        setSingleStudy(response.data.data);
        setError(null);
      } else {
        setError('Case study not found');
      }
    } catch (err) {
      console.error('Error fetching case study:', err);
      setError('Failed to load case study');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?status=published&limit=50`);
      if (response.data.success) {
        setCaseStudies(response.data.data);
        setFilteredStudies(response.data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching case studies:', err);
      setError('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterStudies(term, selectedCategory);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    filterStudies(searchTerm, category);
  };

  const filterStudies = (term, category) => {
    let filtered = caseStudies;

    if (term) {
      filtered = filtered.filter(study =>
        study.title.toLowerCase().includes(term) ||
        study.description?.toLowerCase().includes(term) ||
        study.legal_issue?.toLowerCase().includes(term)
      );
    }

    if (category) {
      filtered = filtered.filter(study => study.category === category);
    }

    setFilteredStudies(filtered);
  };

  const categories = [...new Set(caseStudies.map(s => s.category))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-500">Loading case studies...</span>
      </div>
    );
  }

  // ===== SINGLE CASE STUDY DETAIL VIEW =====
  if (id && singleStudy) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-primary via-primary-light to-[#2a4a6f] text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors">
              <FiArrowLeft className="w-4 h-4" /> Back to Case Studies
            </Link>
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-4">{singleStudy.category}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{singleStudy.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
                <span className="flex items-center gap-1.5"><FiCalendar className="w-4 h-4" />{new Date(singleStudy.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span className="flex items-center gap-1.5"><FiEye className="w-4 h-4" />{singleStudy.views || 0} views</span>
                {singleStudy.tags && <span className="flex items-center gap-1.5"><FiTag className="w-4 h-4" />{singleStudy.tags}</span>}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl shadow-card overflow-hidden">
            {/* Featured Image */}
            {singleStudy.featured_image && (
              <div className="h-72 sm:h-96 overflow-hidden">
                <img src={`${process.env.REACT_APP_API_URL}${singleStudy.featured_image}`} alt={singleStudy.title} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="p-8 sm:p-10 space-y-8">
              {/* Description */}
              {singleStudy.description && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Overview</h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{singleStudy.description}</p>
                </div>
              )}

              {/* Case Background */}
              {singleStudy.case_background && (
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h2 className="text-lg font-bold text-blue-900 mb-3">📋 Case Background</h2>
                  <p className="text-blue-800/80 leading-relaxed whitespace-pre-line">{singleStudy.case_background}</p>
                </div>
              )}

              {/* Legal Issue */}
              {singleStudy.legal_issue && (
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
                  <h2 className="text-lg font-bold text-amber-900 mb-3">⚖️ Legal Issue</h2>
                  <p className="text-amber-800/80 leading-relaxed whitespace-pre-line">{singleStudy.legal_issue}</p>
                </div>
              )}

              {/* Solution Provided */}
              {singleStudy.solution_provided && (
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                  <h2 className="text-lg font-bold text-purple-900 mb-3">💡 Solution Provided</h2>
                  <p className="text-purple-800/80 leading-relaxed whitespace-pre-line">{singleStudy.solution_provided}</p>
                </div>
              )}

              {/* Outcome */}
              {singleStudy.outcome && (
                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <h2 className="text-lg font-bold text-green-900 mb-3">✅ Outcome</h2>
                  <p className="text-green-800/80 leading-relaxed whitespace-pre-line">{singleStudy.outcome}</p>
                </div>
              )}

              {/* Tags */}
              {singleStudy.tags && (
                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                  {singleStudy.tags.split(',').map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-primary/5 text-primary text-sm rounded-lg font-medium">{tag.trim()}</span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <p className="text-gray-500 mb-4">Need help with a similar legal matter?</p>
            <Link to="/booking" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-white bg-primary hover:bg-primary-600 transition-colors shadow-md">
              Book a Consultation <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error for single study
  if (id && error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-card p-12 text-center max-w-md">
          <FiBriefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Case Study Not Found</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/case-studies" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-600 transition-colors">
            <FiArrowLeft className="w-4 h-4" /> Browse All Case Studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary-light to-[#2a4a6f] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Our Case Studies
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" className="text-lg text-white/70 max-w-2xl mx-auto">
            Explore real cases where we've helped clients achieve favorable outcomes
          </motion.p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-card p-5">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search case studies..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryFilter('')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedCategory === '' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Cases
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => handleCategoryFilter(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error && <div className="text-center text-red-500 mb-6">{error}</div>}

        {filteredStudies.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-card p-16 text-center">
            <FiInbox className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-bold text-gray-900 mb-1">No case studies found</h3>
            <p className="text-gray-500">Try adjusting your filters or search term</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudies.map((study, idx) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-2xl shadow-card overflow-hidden hover:shadow-card-hover transition-shadow duration-300 group"
              >
                {/* Featured Image */}
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  {study.featured_image ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${study.featured_image}`}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/5">
                      <FiBriefcase className="w-10 h-10 text-primary/30" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 px-3 py-1 bg-primary text-white text-xs font-bold rounded-full">{study.category}</span>
                </div>

                {/* Card Content */}
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{study.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-3 mb-4">
                    {study.description ? study.description.substring(0, 150) : 'No description available'}...
                  </p>

                  {/* Key Information */}
                  <div className="space-y-2 mb-4">
                    {study.legal_issue && (
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold text-gray-700">Legal Issue:</span> {study.legal_issue.substring(0, 50)}...
                      </p>
                    )}
                    {study.outcome && (
                      <div className="flex items-start gap-1.5 text-xs text-green-700">
                        <FiCheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <p><span className="font-semibold">Outcome:</span> {study.outcome.substring(0, 50)}...</p>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {study.tags && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {study.tags.split(',').slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-primary/5 text-primary text-xs rounded-md font-medium">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {new Date(study.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <Link
                      to={`/case-study/${study.id}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary-light transition-colors"
                    >
                      Read More <FiArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseStudiesPublic;
